import ListItem from '@ahamove/web-components/lib/ListItem';
import ListItemButton from '@ahamove/web-components/lib/ListItemButton';
import ListItemIcon from '@ahamove/web-components/lib/ListItemIcon';
import ListItemText from '@ahamove/web-components/lib/ListItemText';
import SvgIcon from '@ahamove/web-components/lib/SvgIcon';
import Typography from '@ahamove/web-components/lib/Typography';
import dayjs from 'dayjs';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { useGetUserProfile } from '@/api/auth/useGetUserProfile';
import { useDateTimeStore } from '@/global-stores/useDateTimeStore';
import type { DateTimeStore } from '@/global-stores/useDateTimeStore';
import { useLocationStore } from '@/global-stores/useLocationStore';
import { useRentTypeStore } from '@/global-stores/useRentTypeStore';
import { detectPlatform } from '@/lib/common';
import { DEFAULT_DATE_TIME_FORMAT, EV_TRACK_KEY } from '@/lib/constants';
import { trackEvent } from '@/lib/eventTracking';
import { CalendarNoteIcon } from '@/lib/icons';
import { isServer } from '@/lib/isServer';
import { homeConfig } from '@/modules/home/homeConfig';
import DialogRangeTimePicker from '@/shared-components/DialogRangeTimePicker';
import useIsMobile from '@/shared-hooks/useIsMobile';
import { useNewBooking } from '../NewBookingProvider';
import DateTimeRangeCalculation from './DateTimeRangeCalculation';

type Props = { handleShowTooltip: () => void };

export default function DateTimePicker({ handleShowTooltip }: Props) {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const isMobile = useIsMobile();
  const { rentType } = useRentTypeStore();
  const [openDialog, setOpenDialog] = React.useState(false);

  const handleClickOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  React.useEffect(() => {
    if (!isServer) {
      document.body.style.setProperty(
        'overflow',
        openDialog ? 'hidden' : 'auto',
        'important'
      );
    }
  }, [openDialog]);

  const { fareType, startTime, expectedEndTime, setDateTimeStore } =
    useDateTimeStore();
  const { address, city } = useLocationStore();
  const { data: user } = useGetUserProfile();
  const isPickedDateTime = React.useMemo(
    () => startTime && expectedEndTime,
    [startTime, expectedEndTime]
  );

  const { errors } = useNewBooking();

  const requiredAddress = 'location' in errors;
  const requiredTime = 'time' in errors;

  const [activeTab, setActiveTab] = React.useState(
    0 // fareType === 'hourly_fare' ? 1 : 0
  );

  React.useEffect(() => {
    setActiveTab(fareType === 'hourly_fare' ? 1 : 0);
  }, [fareType]);

  // const handleChangeTab = (event: React.SyntheticEvent, tabIndex: number) => {
  //   setActiveTab(tabIndex);
  // };
  const handleChangeDate = (value: DateTimeStore) => {
    setDateTimeStore(value);

    const user_agent = navigator.userAgent;
    const platform = detectPlatform(user_agent);
    trackEvent(EV_TRACK_KEY.ev_time_choose, {
      user_id: user?._id || null,
      userId: user?._id || null,
      platform,
      user_agent,
      city_id: city,
      location: address?.address,
      start_time: dayjs.unix(startTime).format(DEFAULT_DATE_TIME_FORMAT),
      end_time: dayjs.unix(expectedEndTime).format(DEFAULT_DATE_TIME_FORMAT),
      hour_renting: (expectedEndTime - startTime) / 3600,
    });

    handleCloseDialog();
  };

  return (
    <>
      <ListItem
        sx={{
          py: 0,
          height: '100%',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <ListItemButton
          disableRipple
          alignItems="flex-start"
          sx={(theme) => ({
            height: '100%',
            pr: `${theme.spacing(2)} !important`,
          })}
          onClick={() => {
            if (requiredAddress) {
              handleShowTooltip();
            } else {
              handleClickOpenDialog();
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: '38px', paddingRight: 2 }}>
            <SvgIcon
              inheritViewBox
              component={CalendarNoteIcon}
              sx={(theme) => ({
                path: {
                  fill: openDialog
                    ? theme.palette.info.dark
                    : theme.palette.grey[500],
                },
              })}
            />
          </ListItemIcon>
          <ListItemText
            sx={{ mb: 2 }}
            primary={
              <>
                <Typography
                  variant={isMobile ? 'h4' : 'h1'}
                  component="h2"
                  color={openDialog ? 'info.dark' : 'grey.900'}
                >
                  {t('booking:booking_box.rental_time')}
                  {/* {!fareType ? t('booking:booking_box.rental_time') : null}
                  {fareType === 'dynamic_fare'
                    ? t('booking:datetime_picker.flexible_dates')
                    : null}
                  {fareType === 'hourly_fare'
                    ? t('booking:datetime_picker.hourly_rental')
                    : null} */}
                </Typography>
                <Typography
                  mt={1}
                  variant={isMobile ? 'h6' : 'h4'}
                  component="h3"
                  color={
                    requiredTime && !requiredAddress
                      ? 'error.main'
                      : isPickedDateTime
                      ? 'grey.900'
                      : 'grey.500'
                  }
                  fontWeight={isPickedDateTime ? 600 : 500}
                  fontSize={isMobile ? '14px' : requiredAddress ? '20px' : 18}
                >
                  {isPickedDateTime ? (
                    <>
                      {dayjs.unix(startTime).format(DEFAULT_DATE_TIME_FORMAT)}
                      {' - '}
                      {dayjs
                        .unix(expectedEndTime)
                        .format(DEFAULT_DATE_TIME_FORMAT)}
                    </>
                  ) : (
                    <span>{t('booking:booking_box.add_date_time')}</span>
                  )}
                </Typography>
                {isPickedDateTime ? (
                  <Typography
                    variant={isMobile ? 'h6' : 'h4'}
                    component="h3"
                    color={isPickedDateTime ? 'grey.900' : 'grey.500'}
                    fontWeight={500}
                  >
                    <DateTimeRangeCalculation
                      type={rentType}
                      range={[startTime, expectedEndTime]}
                    />
                  </Typography>
                ) : null}
              </>
            }
          />
        </ListItemButton>
      </ListItem>
      <DialogRangeTimePicker
        handleChangeDate={handleChangeDate}
        isOpen={openDialog}
        onClose={handleCloseDialog}
      />
    </>
  );
}
